import React, { useEffect, useRef, useState } from "react";
import styles from "./KanbanBoard.module.scss";
import KanbanColumn from "../../components/KanbanColumn/KanbanColumn";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatShortDate } from "../../utils/helpers";
import { ReactComponent as ArrowRightIcon } from "../../icons/arrow-right-new.svg";
import { ReactComponent as ArrowLeftIcon } from "../../icons/arrow-left-new.svg";
import { pushModal } from "../../redux/modalNavigationStore";
import { hideModal } from "../../redux/modalStore";
import { updateFloatingBarVisible } from "../../redux/layoutStore";
import LargeAuditIcon from "../../icons/large-audit.svg";
import LargeFindingIcon from "../../icons/large-finding.svg";
import LargeImprovementIcon from "../../icons/large-improvement.svg";
import WhiteAuditIcon from "../../icons/white-audit.svg";
import WhiteFindingIcon from "../../icons/white-finding.svg";
import WhiteImprovementIcon from "../../icons/white-improvement.svg";

const STATUSES = ["Open", "In Progress", "In Review", "Done"];

const KanbanBoard = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const externalScrollRef = useRef(null);

  const { findingsData, auditsTasksData, improvementsData } = useSelector(
    (state) => state.organization,
  );
  const userEmail = useSelector((state) => state.user.email);

  const [activeFilters, setActiveFilters] = useState([]);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);

  const handleScroll = () => {
    const el = externalScrollRef.current;
    if (!el) return;

    const atEnd = el.scrollWidth - el.scrollLeft <= el.clientWidth + 5;
    const atStart = el.scrollLeft <= 5;

    setIsScrolledToEnd(atEnd);
    setIsAtStart(atStart);
  };

  const scrollLeft = () => {
    const el = externalScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: -300, behavior: "smooth" });

    // Wait for scroll to finish before checking position
    setTimeout(() => handleScroll(), 300);
  };

  const scrollRight = () => {
    const el = externalScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: 300, behavior: "smooth" });

    // Wait for scroll to finish before checking position
    setTimeout(() => handleScroll(), 300);
  };

  useEffect(() => {
    const handleResize = () => handleScroll();
    window.addEventListener("resize", handleResize);
    handleScroll(); // initial
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLinkedModalOpen = (path) => {
    dispatch(pushModal(window.location.pathname));
    dispatch(hideModal());
    navigate(`/${orgId}/${path}`);
    dispatch(updateFloatingBarVisible(false));
  };

  const toggleFilter = (type) => {
    setActiveFilters((prev) => {
      const isSelected = prev.includes(type);
      return isSelected ? prev.filter((t) => t !== type) : [...prev, type];
    });
  };

  const isUserInvolved = (item, email) => {
    if (!email) return false;

    const roles = [
      item.responsible,
      item.reporter,
      ...(Array.isArray(item.accountable)
        ? item.accountable
        : [item.accountable]),
      ...(Array.isArray(item.consulted) ? item.consulted : [item.consulted]),
      ...(Array.isArray(item.informed) ? item.informed : [item.informed]),
    ];

    return roles.includes(email);
  };

  const getType = (item) => {
    if (item.key?.startsWith("A-")) return "A";
    if (item.key?.startsWith("F-")) return "F";
    if (item.key?.startsWith("I-")) return "I";
    return "?";
  };

  const allTasks = [
    ...Object.values(auditsTasksData || {}).filter(
      (audit) => audit.type !== "external",
    ),
    ...Object.values(findingsData || {}),
    ...Object.values(improvementsData || {}),
  ]
    .filter((item) => !item.archived)
    .filter((item) => isUserInvolved(item, userEmail))
    .filter((item) => {
      // If no filters selected, show all
      if (activeFilters.length === 0) return true;
      return activeFilters.includes(getType(item));
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const groupByStatus = () => {
    const grouped = {
      Open: [],
      "In Progress": [],
      "In Review": [],
      Done: [],
    };

    allTasks.forEach((item) => {
      const status = item.status || "Open";
      if (grouped[status]) {
        grouped[status].push(item);
      }
    });

    return grouped;
  };

  const groupedData = groupByStatus();

  const externalAudits = Object.values(auditsTasksData || {})
    .filter(
      (item) =>
        item.type === "external" && !item.archived && item.status !== "Done",
    )
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const handleAuditClick = (audit) => {
    handleLinkedModalOpen(`audits/${audit.key}`);
  };

  return (
    <div className={styles.kanbanBoard}>
      <div className={styles.topRow}>
        <span className={styles.title}>{t("kanban.allTasksTitle")}</span>
        <div className={styles.filters}>
          <span className={styles.label}>{t("kanban.filterBy")}</span>
          {[
            {
              type: "A",
              labelKey: "kanban.audits",
              icon: LargeAuditIcon,
              activeIcon: WhiteAuditIcon,
            },
            {
              type: "F",
              labelKey: "kanban.findings",
              icon: LargeFindingIcon,
              activeIcon: WhiteFindingIcon,
            },
            {
              type: "I",
              labelKey: "kanban.improvements",
              icon: LargeImprovementIcon,
              activeIcon: WhiteImprovementIcon,
            },
          ].map(({ type, labelKey, icon, activeIcon }) => {
            const isActive = activeFilters.includes(type);
            const IconComponent = isActive ? activeIcon : icon;
            const label = t(labelKey);

            return (
              <button
                key={type}
                className={classNames(
                  styles.filterButton,
                  styles[type],
                  isActive && styles.active,
                )}
                onClick={() => toggleFilter(type)}
              >
                <span className={styles.badge}>
                  <img src={IconComponent} alt={`${label} icon`} />
                </span>
                {label}
              </button>
            );
          })}
        </div>
      </div>
      <div className={styles.columns}>
        {STATUSES.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            items={groupedData[status] || []}
            userEmail={userEmail}
            activeFilters={activeFilters}
          />
        ))}
      </div>
      {externalAudits.length > 0 && (
        <div className={styles.externalAuditsSection}>
          {externalAudits.length > 0 && (
            <div className={styles.externalAuditsSection}>
              <div className={styles.externalAuditsTitle}>
                {t("kanban.upcomingExternalAudits")}
              </div>
              <div className={styles.scrollWrapper}>
                <div
                  className={styles.externalAuditsRow}
                  ref={externalScrollRef}
                  onScroll={handleScroll}
                >
                  {externalAudits.map((audit) => (
                    <div
                      key={audit.key}
                      className={styles.externalAuditCard}
                      onClick={() => handleAuditClick(audit)}
                    >
                      <div className={styles.auditTitle}>
                        {audit.name ||
                          audit.reference ||
                          t("kanban.untitled")}
                      </div>
                      <div className={styles.auditDate}>
                        {t("kanban.dueDate", {
                          date: formatShortDate(audit.date),
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {!isAtStart && (
                  <div
                    className={styles.scrollOverlayLeft}
                    onClick={scrollLeft}
                  >
                    <ArrowLeftIcon className={styles.arrowIcon} />
                  </div>
                )}

                {!isScrolledToEnd && (
                  <div
                    className={styles.scrollOverlayRight}
                    onClick={scrollRight}
                  >
                    <ArrowRightIcon className={styles.arrowIcon} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;
